/* ==========================================================================
   Lumen — predictive-search.js
   <predictive-search> lives inside <lumen-drawer id="SearchDrawer">.
   Debounced fetch of the Section Rendering API (section_id=predictive-search),
   injects the returned results region, keyboard-navigable via
   aria-activedescendant, recent searches in localStorage.
   Progressive enhancement: with JS off the form still submits to /search.
   ========================================================================== */
(function () {
  'use strict';

  var RECENT_KEY = 'lumen:recent-searches';
  var RECENT_MAX = 6;

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      return list.filter(function (t) { return typeof t === 'string' && t.trim().length; }).slice(0, RECENT_MAX);
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage unavailable (private mode) — recent searches just won't persist */
    }
  }

  if (customElements.get('predictive-search')) return;

  customElements.define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-ps-input]');
      this.form = this.querySelector('form[role="search"]');
      this.results = this.querySelector('[data-ps-results]');
      this.defaultView = this.querySelector('[data-ps-default]');
      this.loading = this.querySelector('[data-ps-loading]');
      this.status = this.querySelector('[data-ps-status]');
      this.recentWrap = this.querySelector('[data-ps-recent]');
      this.recentList = this.querySelector('[data-ps-recent-list]');
      this.resetBtn = this.querySelector('[data-ps-reset]');

      this.searchUrl = this.getAttribute('data-url')
        || (window.theme && window.theme.routes && window.theme.routes.predictive_search_url)
        || '/search/suggest';
      this.controller = null;
      this.options = [];
      this.activeIndex = -1;

      if (!this.input || !this.results) return;
      var self = this;

      var onInput = this.onInput.bind(this);
      var debounced = (window.theme && window.theme.utils && window.theme.utils.debounce)
        ? window.theme.utils.debounce(onInput, 300)
        : onInput;
      this.input.addEventListener('input', debounced);
      // Instant (non-debounced) UI feedback for the reset button.
      this.input.addEventListener('input', function () {
        if (self.resetBtn) self.resetBtn.hidden = self.input.value.trim().length === 0;
      });
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', function () {
          self.input.value = '';
          self.resetBtn.hidden = true;
          self.reset();
          self.input.focus();
        });
      }

      // Delegated: trending pills (data-ps-term), recent pills, clear button.
      this.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-ps-term]');
        if (pill) {
          e.preventDefault();
          self.applyTerm(pill.getAttribute('data-ps-term'));
          return;
        }
        if (e.target.closest('[data-ps-recent-clear]')) {
          writeRecent([]);
          self.renderRecent();
          self.input.focus();
        }
      });

      this.renderRecent();

      // Drawer integration: autofocus on open, clean Escape close.
      var drawer = this.closest('lumen-drawer');
      if (drawer) {
        drawer.addEventListener('dialog:open', function () {
          window.requestAnimationFrame(function () {
            self.input.focus();
            self.input.select();
          });
        });
        var dialog = drawer.querySelector('dialog');
        if (dialog) {
          // Route the native Esc cancel through the drawer's close() so the
          // page scroll lock is released and dialog:close fires.
          dialog.addEventListener('cancel', function (e) {
            e.preventDefault();
            if (typeof drawer.close === 'function') drawer.close();
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    applyTerm(term) {
      if (!term) return;
      this.input.value = term;
      if (this.resetBtn) this.resetBtn.hidden = false;
      this.input.focus();
      this.onInput();
    }

    onInput() {
      var q = this.input.value.trim();
      if (!q.length) {
        this.reset();
        return;
      }
      this.fetchResults(q);
    }

    reset() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.defaultView) this.defaultView.hidden = false;
      if (this.loading) this.loading.hidden = true;
      if (this.status) this.status.textContent = '';
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    fetchResults(q) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var self = this;
      if (this.loading) this.loading.hidden = false;

      var url = this.searchUrl
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search';

      fetch(url, { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Predictive search failed: ' + res.status);
          return res.text();
        })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          self.renderResults(doc.querySelector('[data-ps-results-inner]'));
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (self.loading) self.loading.hidden = true;
        });
    }

    renderResults(inner) {
      if (this.loading) this.loading.hidden = true;
      this.results.innerHTML = inner ? inner.innerHTML : '';
      this.results.hidden = false;
      if (this.defaultView) this.defaultView.hidden = true;
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      this.options = Array.prototype.slice.call(this.results.querySelectorAll('[data-ps-option]'));
      this.options.forEach(function (opt, i) {
        opt.id = 'PredictiveSearchOption-' + i;
        opt.setAttribute('aria-selected', 'false');
      });
      this.input.setAttribute('aria-expanded', String(this.options.length > 0));

      if (this.status) {
        var live = this.results.querySelector('[data-ps-live-text]');
        this.status.textContent = live ? live.textContent.trim() : '';
      }
    }

    setActive(i) {
      if (!this.options.length) return;
      this.options.forEach(function (opt, j) {
        opt.classList.toggle('is-active', j === i);
        opt.setAttribute('aria-selected', String(j === i));
      });
      this.activeIndex = i;
      var opt = this.options[i];
      this.input.setAttribute('aria-activedescendant', opt.id);
      if (typeof opt.scrollIntoView === 'function') opt.scrollIntoView({ block: 'nearest' });
    }

    move(dir) {
      if (!this.options.length) return;
      var i = this.activeIndex + dir;
      if (i < 0) i = this.options.length - 1;
      if (i >= this.options.length) i = 0;
      this.setActive(i);
    }

    onKeydown(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (this.results.hidden || !this.options.length) return;
        e.preventDefault();
        this.move(e.key === 'ArrowDown' ? 1 : -1);
      } else if (e.key === 'Enter') {
        if (this.activeIndex > -1 && this.options[this.activeIndex]) {
          e.preventDefault();
          this.saveRecent(this.input.value);
          window.location.href = this.options[this.activeIndex].href;
        }
        // No active option: let the form submit to /search (onSubmit saves recent).
      }
      // Escape: native <dialog> cancel — routed through the drawer close above.
    }

    onSubmit(e) {
      var q = this.input.value.trim();
      if (!q.length) {
        e.preventDefault();
        return;
      }
      this.saveRecent(q);
    }

    saveRecent(q) {
      q = (q || '').trim();
      if (!q.length) return;
      var lower = q.toLowerCase();
      var list = readRecent().filter(function (t) { return t.toLowerCase() !== lower; });
      list.unshift(q);
      writeRecent(list);
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recentWrap || !this.recentList) return;
      var list = readRecent();
      this.recentList.innerHTML = '';
      if (!list.length) {
        this.recentWrap.hidden = true;
        return;
      }
      var frag = document.createDocumentFragment();
      list.forEach(function (term) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'predictive-search__pill';
        btn.setAttribute('data-ps-term', term);
        btn.textContent = term;
        li.appendChild(btn);
        frag.appendChild(li);
      });
      this.recentList.appendChild(frag);
      this.recentWrap.hidden = false;
    }
  });
})();
